/**
 * The one identity band for CalendarPage/AlertPage, replacing the old dual
 * header (shell PageHeader printing the name once, the builder printing it
 * again 40px below with an orphaned status badge) and the mismatched-height
 * floating controls.
 * Spec: assets/handoffs/design_handoff_builder_header — this is the
 * "interim" band, a stop-gap ahead of the real builder header redesign.
 *
 * Same component at both widths ("Rules — mobile band"): below 768px,
 * Preview moves into the kebab menu (the settings toggle keeps its slot —
 * it is the point of the page) and the row shrinks from 68px to 60px.
 *
 * @param {string} name - already resolved to the fallback ("Untitled
 *   calendar"/"Untitled alert") by the caller when never saved
 * @param {boolean=false} untitled - true renders `name` in the placeholder style
 * @param {boolean} published
 * @param {string} indexHref - route back to the widget index
 * @param {string} indexLabel - "Calendars" / "Event alerts", used in the
 *   back button's title/aria-label and the mobile kebab's "{indexLabel}
 *   settings" entries below
 * @param {function} onPreview
 * @param {function} onDelete
 * @param {boolean} panelHidden - desktop RHS settings panel visibility
 * @param {function} onTogglePanel - desktop only
 * @param {boolean} settingsOpen - mobile settings sheet visibility
 * @param {function} onToggleSettings - mobile only
 * @param {boolean=false} locked - true while the widget has no integration
 *   attached: Preview and the panel/settings toggle render disabled with a
 *   tooltip explaining why, but stay mounted (never hidden) so nothing
 *   shifts once they re-enable. More actions/kebab (and Delete inside it)
 *   stays fully enabled.
 * @param {boolean=false} previewDisabled - independent of `locked`: disables
 *   just the Preview control (e.g. pre-creation, where there is nothing to
 *   preview yet but the panel toggle should stay fully usable).
 */
import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";
import Icon from "../../../theme/untitled/icons/Icon";
import Badge from "../../../theme/untitled/Badge/Badge";
import { useDismissablePanel } from "../../../templates/AppShell/useDismissablePanel";
import useWindowSize from "../../../../../hooks/useWindowSize";
import styles from "./BuilderIdentityBand.module.scss";

const MOBILE_BREAKPOINT = 768;

const BuilderIdentityBand = ({
  name,
  untitled = false,
  published,
  indexHref,
  indexLabel,
  onPreview,
  onDelete,
  panelHidden,
  onTogglePanel,
  settingsOpen,
  onToggleSettings,
  locked = false,
  previewDisabled = false,
}) => {
  const { width } = useWindowSize();
  const isMobile = width !== undefined && width < MOBILE_BREAKPOINT;
  const { open: menuOpen, setOpen: setMenuOpen, triggerRef, panelRef, close } = useDismissablePanel();
  const lockedTooltip = "Available once an integration is attached";
  const previewInactive = locked || previewDisabled;
  const previewTooltip = locked
    ? lockedTooltip
    : previewDisabled
    ? "Available once the calendar is created"
    : undefined;
  const kebabHasItems = isMobile || Boolean(onDelete);

  const toggleKebabMenu = useCallback(() => setMenuOpen((o) => !o), [setMenuOpen]);

  const onPreviewMenuItemClick = useCallback(() => {
    if (previewInactive) {
      return;
    }
    close(false);
    onPreview?.();
  }, [previewInactive, close, onPreview]);

  const onDeleteMenuItemClick = useCallback(
    (e) => {
      close(false);
      onDelete(e);
    },
    [close, onDelete]
  );

  const backLabel = `Back to ${indexLabel}`;
  const back = (
    <Link
      to={indexHref}
      title={backLabel}
      aria-label={backLabel}
      className={classNames(styles.iconBtn, { [styles["iconBtn--mobile"]]: isMobile })}
    >
      <Icon name="ArrowLeft" size={20} />
    </Link>
  );

  const badge = (
    <Badge color={published ? "success" : "gray"} dot className={styles.badge}>
      {published ? "Published" : "Unpublished"}
    </Badge>
  );

  const identity = (
    <div className={styles.id}>
      <h1 title={name} className={classNames(styles.title, { [styles["title--untitled"]]: untitled })}>
        {name}
      </h1>
      {badge}
    </div>
  );

  const kebabLabel = "More actions";
  const kebabButton = (
    <button
      ref={triggerRef}
      type="button"
      disabled={!kebabHasItems}
      title={kebabHasItems ? kebabLabel : undefined}
      aria-label={kebabLabel}
      aria-haspopup="menu"
      aria-expanded={menuOpen}
      className={classNames(styles.iconBtn, {
        [styles["iconBtn--mobile"]]: isMobile,
        [styles["iconBtn--disabled"]]: !kebabHasItems,
      })}
      onClick={kebabHasItems ? toggleKebabMenu : undefined}
    >
      <Icon name="DotsVertical" size={20} />
    </button>
  );

  const kebabMenu = menuOpen && kebabHasItems && (
    <div ref={panelRef} role="menu" className={styles.menu}>
      {isMobile && (
        <button
          type="button"
          role="menuitem"
          disabled={previewInactive}
          title={previewInactive ? previewTooltip : undefined}
          className={classNames(styles.menuItem, { [styles["menuItem--disabled"]]: previewInactive })}
          onClick={onPreviewMenuItemClick}
        >
          <i className={classNames("material-icons", styles.menuIcon)}>preview</i>
          Preview
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          role="menuitem"
          className={classNames(styles.menuItem, styles["menuItem--danger"])}
          onClick={onDeleteMenuItemClick}
        >
          <i className={classNames("material-icons", styles.menuIcon)}>delete</i>
          Delete widget
        </button>
      )}
    </div>
  );

  if (isMobile) {
    const settingsLabel = settingsOpen ? "Hide settings" : `${indexLabel.replace(/s$/, "")} settings`;
    return (
      <div className={styles.mobileBand}>
        {back}
        {identity}
        <div className={styles.mobileGroup}>
          <div className={styles.menuAnchor}>
            {kebabButton}
            {kebabMenu}
          </div>
          <button
            type="button"
            disabled={locked}
            title={locked ? lockedTooltip : settingsLabel}
            aria-label={locked ? lockedTooltip : settingsLabel}
            aria-pressed={settingsOpen}
            className={classNames(styles.iconBtn, styles["iconBtn--mobile"], {
              [styles["iconBtn--on"]]: settingsOpen,
              [styles["iconBtn--disabled"]]: locked,
            })}
            onClick={locked ? undefined : onToggleSettings}
          >
            <Icon name="LayoutRight" size={20} />
          </button>
        </div>
      </div>
    );
  }

  const panelToggleLabel = panelHidden ? "Show panel" : "Hide panel";
  return (
    <div className={styles.band}>
      {back}
      {identity}
      <div className={styles.actions}>
        <button
          type="button"
          disabled={previewInactive}
          title={previewTooltip}
          className={classNames(styles.previewBtn, { [styles["previewBtn--disabled"]]: previewInactive })}
          onClick={previewInactive ? undefined : onPreview}
        >
          <i className="material-icons">preview</i>
          Preview
        </button>
        <div className={styles.pair}>
          <div className={styles.menuAnchor}>
            {kebabButton}
            {kebabMenu}
          </div>
          <button
            type="button"
            disabled={locked}
            title={locked ? lockedTooltip : panelToggleLabel}
            aria-label={locked ? lockedTooltip : panelToggleLabel}
            aria-pressed={panelHidden}
            className={classNames(styles.iconBtn, {
              [styles["iconBtn--on"]]: panelHidden,
              [styles["iconBtn--disabled"]]: locked,
            })}
            onClick={locked ? undefined : onTogglePanel}
          >
            <Icon
              name="LayoutRight"
              size={20}
              className={classNames({ [styles.mirrored]: panelHidden })}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuilderIdentityBand;
